import { useState, useEffect, useCallback } from 'react';
import API from '../helpers/axiosInstance';
import { getApiHeaders, hasPermission } from '../utils/DefaultFunction';
import { getCachedPermissions, hasCachedPermissions, setCachedPermissions } from '../utils/permissionCache';

// Standardises the permission-fetch boilerplate that used to be hand-rolled
// (and frequently wrong — derived from location.pathname, which breaks on
// trailing slashes and :id sub-routes) on every gated page. Always pass a
// hardcoded group slug — the page's own base route — never the current path.
//
//   const { loading, can } = usePagePermissions('marketing/cohorts');
//   if (loading) return <Spinner />;
//   if (!can('cohort-list')) return <NoAccess />;
//
// `loading` starts `true` so callers never have a frame where an unresolved
// permission check reads as "denied" (the flash-of-No-Access bug class).
//
// PERM-159: resolved permission lists rarely change within a session, but
// some callers (e.g. Header) get re-mounted on nearly every page navigation,
// which used to mean a fresh network round-trip for the same group every
// time. Cache the resolved list per group slug (in src/utils/permissionCache.js,
// cleared on logout/login — see PERM-178) so a repeat mount can resolve
// synchronously from cache instead of refetching.
export { clearPermissionCache } from '../utils/permissionCache';

export default function usePagePermissions(groupSlug) {
    const cached = groupSlug ? getCachedPermissions(groupSlug) : undefined;
    const [permissions, setPermissions] = useState(cached || []);
    const [loading, setLoading] = useState(Boolean(groupSlug) && cached === undefined);

    useEffect(() => {
        if (!groupSlug) {
            setPermissions([]);
            setLoading(false);
            return;
        }
        if (hasCachedPermissions(groupSlug)) {
            // Already resolved by an earlier mount — use it synchronously,
            // no loading spinner flash.
            setPermissions(getCachedPermissions(groupSlug));
            setLoading(false);
            return;
        }
        let cancelled = false;
        const fetchPermissions = async () => {
            setLoading(true);
            try {
                const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${groupSlug}`;
                const response = await API.get(url, { headers: getApiHeaders() });
                const data = response.data.data || [];
                if (!cancelled) {
                    setCachedPermissions(groupSlug, data);
                    setPermissions(data);
                }
            } catch (error) {
                console.error('Error fetching permissions:', error);
                if (!cancelled) setPermissions([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchPermissions();
        return () => { cancelled = true; };
    }, [groupSlug]);

    const can = useCallback((slug) => hasPermission(slug, permissions), [permissions]);

    return { permissions, loading, can };
}
